import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Text,
  Checkbox,
  Alert,
} from "@mantine/core";
import { IconLock, IconAlertCircle } from "@tabler/icons-react";
import { useAuthStore } from "../../store";

interface SignOffAuthModalProps {
  opened: boolean;
  onClose: () => void;
  onAuthenticate: (password: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function SignOffAuthModal({
  opened,
  onClose,
  onAuthenticate,
  isLoading = false,
  error = null,
}: SignOffAuthModalProps) {
  const [password, setPassword] = useState("");
  const [rememberSession, setRememberSession] = useState(true);
  const profile = useAuthStore((state) => state.profile);

  // Reset password when modal opens
  useEffect(() => {
    if (opened) {
      setPassword("");
    }
  }, [opened]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    await onAuthenticate(password);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconLock size={20} />
          <span>Authentication Required</span>
        </Group>
      }
      size="sm"
      centered
      closeOnClickOutside={false}
      closeOnEscape={!isLoading}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Sign-out requires authentication for the first case of your session.
          </Text>

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Authentication Failed"
              color="red"
            >
              {error}
            </Alert>
          )}

          <TextInput
            label="Username"
            value={profile?.username || profile?.email || ""}
            disabled
            styles={{
              input: { backgroundColor: "#f5f5f5" },
            }}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
            autoFocus
            error={error ? " " : undefined}
          />

          <Checkbox
            label="Remember for this session (4 hours)"
            checked={rememberSession}
            onChange={(e) => setRememberSession(e.currentTarget.checked)}
            size="sm"
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              Sign Out
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
